import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Upload, Button, Progress, message, Typography } from 'antd';
import { InboxOutlined, CloseOutlined, CheckOutlined, WarningOutlined, UploadOutlined } from '@ant-design/icons';
import { useDropzone } from 'react-dropzone';
import { imageService } from '@src/services/image.service';
import { createPreviewUrl, revokePreviewUrl, formatFileSize } from '@src/utils/image-utils';
import type { UploadFile } from '../types';

const { Dragger } = Upload;
const { Text } = Typography;

interface ImageUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  patientId: number;
  category: string;
  onUpload: (files: UploadFile[]) => Promise<any>;
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
  thumbnailUrl?: string;
  previewUrl?: string;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  patientId,
  category,
  onUpload,
}) => {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Cleanup URLs when component unmounts or modal closes
  useEffect(() => {
    return () => {
      uploadProgress.forEach(item => {
        if (item.previewUrl) {
          revokePreviewUrl(item.previewUrl);
        }
        if (item.thumbnailUrl) {
          revokePreviewUrl(item.thumbnailUrl);
        }
      });
    };
  }, [uploadProgress]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setIsUploading(true);
      
      // Create initial progress entries with preview URLs
      const newProgress: UploadProgress[] = acceptedFiles.map(file => ({
        file,
        progress: 0,
        status: 'uploading',
        previewUrl: createPreviewUrl(file),
      }));
      setUploadProgress(newProgress);

      try {
        // Upload files one by one with individual progress tracking
        const results = [];
        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];
          const uploadFile: UploadFile = { file, category };
          
          // Simulate progress for this specific file
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => 
              prev.map((item, index) => 
                index === i 
                  ? { ...item, progress: Math.min(item.progress + Math.random() * 15, 90) }
                  : item
              )
            );
          }, 300);

          try {
            // Upload single file
            const result = await onUpload([uploadFile]);
            results.push(result);
            
            // Clear progress interval for this file
            clearInterval(progressInterval);
            
            // Mark this file as success
            setUploadProgress(prev => 
              prev.map((item, index) => 
                index === i 
                  ? { ...item, status: 'success' as const, progress: 100 }
                  : item
              )
            );
          } catch (error) {
            // Clear progress interval for this file
            clearInterval(progressInterval);
            
            // Mark this file as error
            setUploadProgress(prev => 
              prev.map((item, index) => 
                index === i 
                  ? { 
                      ...item, 
                      status: 'error' as const, 
                      error: error instanceof Error ? error.message : 'Upload failed' 
                    }
                  : item
              )
            );
            
            console.error(`File ${i + 1} upload failed:`, file.name, error);
            throw error; // Re-throw to stop the loop
          }
        }
        
        message.success(`${acceptedFiles.length} image(s) uploaded successfully!`);
        
        // Clear uploaded images and close modal after a short delay
        setTimeout(() => {
          setUploadProgress([]);
          setIsUploading(false);
          onSuccess();
          onCancel();
        }, 1500);
      } catch (error) {
        console.error('Upload error:', error);
        message.error('Some images failed to upload');
        setIsUploading(false);
      }
    },
    [onUpload, onSuccess, onCancel, category]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    disabled: isUploading,
  });

  const removeFile = (index: number) => {
    setUploadProgress(prev => {
      const item = prev[index];
      // Clean up URLs to prevent memory leaks
      if (item.previewUrl) {
        revokePreviewUrl(item.previewUrl);
      }
      if (item.thumbnailUrl) {
        revokePreviewUrl(item.thumbnailUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const retryUpload = (index: number) => {
    const file = uploadProgress[index].file;
    onDrop([file]);
  };

  const handleCancel = () => {
    if (!isUploading) {
      onCancel();
    }
  };

  return (
    <Modal
      title="Upload Images"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <div className="space-y-6">
        {/* Drop Zone - Hidden during upload */}
        {!isUploading && (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            <InboxOutlined className="text-4xl text-gray-400 mb-4" />
            <Text className="text-lg font-medium text-gray-900 block mb-2">
              {isDragActive ? 'Drop images here' : 'Drag & drop images here'}
            </Text>
            <Text className="text-sm text-gray-500 block mb-4">
              or click to select files
            </Text>
            <Text className="text-xs text-gray-400 block">
              Supports: JPG, PNG, GIF, WebP (Max 10MB each)
            </Text>
          </div>
        )}

        {/* Upload Progress */}
        {uploadProgress.length > 0 && (
          <div className="space-y-4">
            <Text className="font-medium text-gray-900 text-base">Upload Progress</Text>
            {uploadProgress.map((item, index) => (
              <div
                key={`${item.file.name}-${index}`}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {item.status === 'success' && (
                      <CheckOutlined className="text-green-500 text-lg" />
                    )}
                    {item.status === 'error' && (
                      <WarningOutlined className="text-red-500 text-lg" />
                    )}
                    {item.status === 'uploading' && (
                      <div className="w-5 h-5 animate-spin rounded-full border-2 border-blue-500 border-r-transparent" />
                    )}
                  </div>

                  {/* Thumbnail */}
                  <div className="flex-shrink-0 relative w-12 h-12 bg-gray-200 rounded-lg overflow-hidden">
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt={item.file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <UploadOutlined className="text-gray-400 text-lg" />
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <Text className="text-sm font-medium text-gray-900 truncate block">
                      {item.file.name}
                    </Text>
                    <Text className="text-xs text-gray-500 block">
                      {formatFileSize(item.file.size)}
                    </Text>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {item.status === 'uploading' && (
                    <div className="flex items-center gap-2">
                      <Progress 
                        type="circle" 
                        size={24} 
                        percent={Math.round(item.progress)} 
                        format={(percent) => `${percent}%`}
                        strokeColor="#1890ff"
                      />
                    </div>
                  )}
                  {item.status === 'success' && (
                    <Text className="text-sm text-green-600 font-medium">
                      Complete
                    </Text>
                  )}
                  {item.status === 'error' && (
                    <Button
                      size="small"
                      onClick={() => retryUpload(index)}
                      disabled={isUploading}
                    >
                      Retry
                    </Button>
                  )}
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => removeFile(index)}
                    disabled={isUploading}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button onClick={handleCancel} disabled={isUploading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};
